import * as React from 'react';
import { Columns2, Combine, Plus, Trash2 } from 'lucide-react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import {
  SECTION_SPLIT_COLUMN_COUNT,
  columnWidthAtIndex,
  isBlockAllowedInColumn,
  type Block,
  type BlockType,
  type SectionSplit,
} from '@pageboss/shared';
import { SortableBlock } from './sortable-block';
import { InsertPoint } from './insert-point';
import { SplitPopover } from './split-popover';
import type { BlockOnWire, SectionOnWire } from '@/lib/types';
import { cn } from '@/lib/utils';

interface SectionShellProps {
  section: SectionOnWire;
  selectedBlockId: string | null;
  onSelectBlock: (blockId: string) => void;
  onChangeBlock: (columnIndex: number, blockId: string, nextProps: Record<string, unknown>) => void;
  onMoveBlockUp: (columnIndex: number, blockIndex: number) => void;
  onMoveBlockDown: (columnIndex: number, blockIndex: number) => void;
  onDuplicateBlock: (columnIndex: number, blockId: string) => void;
  onDeleteBlock: (columnIndex: number, blockId: string) => void;
  // Block insert request — caller opens the picker and resolves with
  // a chosen type via `onInsertBlock`.
  onRequestPicker: (
    columnIndex: number,
    insertIndex: number,
    anchorRect: DOMRect,
  ) => void;
  // Section-level actions
  onSplitRow: (nextSplit: SectionSplit) => void;
  onMergeRow: () => void;
  onDeleteSection: () => void;
}

const GRID_CLASS: Record<SectionSplit, string> = {
  full: 'grid-cols-1',
  '2-1': 'grid-cols-1 md:grid-cols-[2fr_1fr]',
  '1-2': 'grid-cols-1 md:grid-cols-[1fr_2fr]',
  '1-1': 'grid-cols-1 md:grid-cols-2',
  '1-1-1': 'grid-cols-1 md:grid-cols-3',
};

export function SectionShell({
  section,
  selectedBlockId,
  onSelectBlock,
  onChangeBlock,
  onMoveBlockUp,
  onMoveBlockDown,
  onDuplicateBlock,
  onDeleteBlock,
  onRequestPicker,
  onSplitRow,
  onMergeRow,
  onDeleteSection,
}: SectionShellProps) {
  const isMultiColumn = section.split !== 'full';
  const columnCount = SECTION_SPLIT_COLUMN_COUNT[section.split];

  // Sanity: clamp columns array to the split's column count. This
  // protects against any drift between section.split and section.columns.
  const columns: BlockOnWire[][] = Array.from({ length: columnCount }, (_, i) =>
    section.columns[i] ?? [],
  );

  const [splitOpen, setSplitOpen] = React.useState<DOMRect | null>(null);

  return (
    <section
      data-section-id={section.id}
      className={cn(
        'group/section relative rounded-2xl border border-transparent px-1 py-1 transition-[border-color]',
        'focus-within:border-border-subtle hover:border-border-subtle',
      )}
    >
      <SectionToolbar
        isMultiColumn={isMultiColumn}
        onOpenSplit={(rect) => setSplitOpen(rect)}
        onMerge={onMergeRow}
        onDelete={onDeleteSection}
      />

      <div className={cn('grid gap-4 md:gap-6', GRID_CLASS[section.split])}>
        {columns.map((blocks, columnIndex) => (
          <ColumnShell
            key={`${section.id}:${columnIndex}`}
            sectionId={section.id}
            split={section.split}
            columnIndex={columnIndex}
            blocks={blocks}
            selectedBlockId={selectedBlockId}
            onSelectBlock={onSelectBlock}
            onChangeBlock={(blockId, props) => onChangeBlock(columnIndex, blockId, props)}
            onMoveBlockUp={(blockIndex) => onMoveBlockUp(columnIndex, blockIndex)}
            onMoveBlockDown={(blockIndex) => onMoveBlockDown(columnIndex, blockIndex)}
            onDuplicateBlock={(blockId) => onDuplicateBlock(columnIndex, blockId)}
            onDeleteBlock={(blockId) => onDeleteBlock(columnIndex, blockId)}
            onRequestPicker={(insertIndex, rect) =>
              onRequestPicker(columnIndex, insertIndex, rect)
            }
          />
        ))}
      </div>

      <SplitPopover
        open={splitOpen !== null}
        anchorRect={splitOpen}
        onClose={() => setSplitOpen(null)}
        onPick={(s) => {
          setSplitOpen(null);
          onSplitRow(s);
        }}
        title="Split this row into"
      />
    </section>
  );
}

interface SectionToolbarProps {
  isMultiColumn: boolean;
  onOpenSplit: (rect: DOMRect) => void;
  onMerge: () => void;
  onDelete: () => void;
}

function SectionToolbar({
  isMultiColumn,
  onOpenSplit,
  onMerge,
  onDelete,
}: SectionToolbarProps) {
  const splitTriggerRef = React.useRef<HTMLButtonElement | null>(null);

  return (
    <div
      className={cn(
        'absolute -top-3.5 right-3 z-10 flex items-center gap-px rounded-md border border-border bg-card shadow-sm',
        'opacity-0 transition-opacity',
        // Reveal when the parent section is hovered/focused, or when the
        // toolbar itself is (so hovering directly on a toolbar button
        // keeps it visible).
        'group-hover/section:opacity-100 group-focus-within/section:opacity-100',
        'hover:opacity-100 focus-within:opacity-100',
      )}
      onClick={(e) => e.stopPropagation()}
    >
      <ToolbarButton
        ref={splitTriggerRef}
        label="Split this row"
        onClick={() => {
          const rect = splitTriggerRef.current?.getBoundingClientRect();
          if (rect) onOpenSplit(rect);
        }}
      >
        <Columns2 className="h-4 w-4" />
      </ToolbarButton>
      {isMultiColumn ? (
        <ToolbarButton label="Merge into one column" onClick={onMerge}>
          <Combine className="h-4 w-4" />
        </ToolbarButton>
      ) : null}
      <ToolbarButton label="Delete this row" onClick={onDelete} variant="danger">
        <Trash2 className="h-4 w-4" />
      </ToolbarButton>
    </div>
  );
}

interface ColumnShellProps {
  sectionId: string;
  split: SectionSplit;
  columnIndex: number;
  blocks: BlockOnWire[];
  selectedBlockId: string | null;
  onSelectBlock: (blockId: string) => void;
  onChangeBlock: (blockId: string, nextProps: Record<string, unknown>) => void;
  onMoveBlockUp: (blockIndex: number) => void;
  onMoveBlockDown: (blockIndex: number) => void;
  onDuplicateBlock: (blockId: string) => void;
  onDeleteBlock: (blockId: string) => void;
  onRequestPicker: (insertIndex: number, anchorRect: DOMRect) => void;
}

// Encode a column drop target as `col:<sectionId>:<columnIndex>` so
// onDragEnd in page-edit can detect "dropped on an empty column" via
// the `over.id` string. Block IDs themselves remain raw ULIDs.
export function columnDroppableId(sectionId: string, columnIndex: number): string {
  return `col:${sectionId}:${columnIndex}`;
}

export function parseColumnDroppableId(
  id: string,
): { sectionId: string; columnIndex: number } | null {
  if (!id.startsWith('col:')) return null;
  const rest = id.slice(4);
  const lastColon = rest.lastIndexOf(':');
  if (lastColon === -1) return null;
  const sectionId = rest.slice(0, lastColon);
  const columnIndex = Number.parseInt(rest.slice(lastColon + 1), 10);
  if (!sectionId || Number.isNaN(columnIndex)) return null;
  return { sectionId, columnIndex };
}

function ColumnShell({
  sectionId,
  split,
  columnIndex,
  blocks,
  selectedBlockId,
  onSelectBlock,
  onChangeBlock,
  onMoveBlockUp,
  onMoveBlockDown,
  onDuplicateBlock,
  onDeleteBlock,
  onRequestPicker,
}: ColumnShellProps) {
  const isMultiColumn = split !== 'full';
  const blockIds = React.useMemo(() => blocks.map((b) => b.id), [blocks]);
  const columnWidth = columnWidthAtIndex(split, columnIndex);

  const dropId = columnDroppableId(sectionId, columnIndex);
  const { setNodeRef: setDropRef, isOver } = useDroppable({ id: dropId });

  if (blocks.length === 0) {
    return (
      <EmptyColumnPlaceholder
        droppableRef={setDropRef}
        sectionId={sectionId}
        columnIndex={columnIndex}
        columnWidth={columnWidth}
        isMultiColumn={isMultiColumn}
        isOver={isOver}
        onAdd={(rect) => onRequestPicker(0, rect)}
      />
    );
  }

  return (
    <div
      ref={setDropRef}
      data-column-id={dropId}
      className={cn(
        '@container/col min-w-0 rounded-xl border-2 border-dashed border-transparent transition-colors',
        isMultiColumn && 'p-1',
        isOver && 'border-brand-500 bg-brand-50/40',
      )}
    >
      <SortableContext items={blockIds} strategy={verticalListSortingStrategy}>
        <ol className="space-y-3">
          {blocks.map((block, index) => (
            <li key={block.id}>
              <InsertPoint
                onClick={(rect) => onRequestPicker(index, rect)}
              />
              <SortableBlock
                block={block as unknown as Block}
                selected={selectedBlockId === block.id}
                canMoveUp={index > 0}
                canMoveDown={index < blocks.length - 1}
                onSelect={() => onSelectBlock(block.id)}
                onChange={(nextProps) => onChangeBlock(block.id, nextProps)}
                onMoveUp={() => onMoveBlockUp(index)}
                onMoveDown={() => onMoveBlockDown(index)}
                onDuplicate={() => onDuplicateBlock(block.id)}
                onDelete={() => onDeleteBlock(block.id)}
              />
            </li>
          ))}
        </ol>
      </SortableContext>
      <InsertPoint
        variant="always"
        label={isMultiColumn ? 'Add to this column' : 'Add block at end'}
        onClick={(rect) => onRequestPicker(blocks.length, rect)}
      />
    </div>
  );
}

interface EmptyColumnPlaceholderProps {
  droppableRef: (el: HTMLElement | null) => void;
  sectionId: string;
  columnIndex: number;
  columnWidth: ReturnType<typeof columnWidthAtIndex>;
  isMultiColumn: boolean;
  isOver: boolean;
  onAdd: (rect: DOMRect) => void;
}

function EmptyColumnPlaceholder({
  droppableRef,
  sectionId,
  columnIndex,
  columnWidth,
  isMultiColumn,
  isOver,
  onAdd,
}: EmptyColumnPlaceholderProps) {
  const triggerRef = React.useRef<HTMLButtonElement | null>(null);
  return (
    <div
      ref={droppableRef}
      data-column-id={columnDroppableId(sectionId, columnIndex)}
      className={cn(
        '@container/col flex min-h-32 items-center justify-center rounded-xl border border-dashed bg-surface-sunken/40 p-4 transition-colors',
        isOver ? 'border-brand-500 bg-brand-50/50' : 'border-border-subtle',
      )}
    >
      <button
        ref={triggerRef}
        type="button"
        onClick={() => {
          const rect = triggerRef.current?.getBoundingClientRect();
          if (rect) onAdd(rect);
        }}
        className={cn(
          'inline-flex items-center gap-2 rounded-full border border-dashed border-border bg-card px-4 py-2 text-sm font-medium text-text-secondary',
          'transition-colors hover:border-brand-500 hover:text-brand-700',
        )}
      >
        <Plus className="h-4 w-4" />
        {isMultiColumn ? 'Add block here' : 'Add block'}
      </button>
    </div>
  );
}

interface ToolbarButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  variant?: 'default' | 'danger';
}

const ToolbarButton = React.forwardRef<HTMLButtonElement, ToolbarButtonProps>(
  function ToolbarButton({ label, variant = 'default', className, children, ...rest }, ref) {
    return (
      <button
        ref={ref}
        type="button"
        aria-label={label}
        title={label}
        className={cn(
          'flex h-11 w-11 items-center justify-center text-text-secondary first:rounded-l-md last:rounded-r-md',
          'transition-colors hover:bg-surface-sunken hover:text-text-primary',
          variant === 'danger' && 'hover:bg-danger-bg hover:text-danger-fg',
          className,
        )}
        {...rest}
      >
        {children}
      </button>
    );
  },
);

// Helper exported for use by the block picker filtering layer.
export function allowedBlockTypesForColumn(
  split: SectionSplit,
  columnIndex: number,
  allTypes: readonly BlockType[],
): BlockType[] {
  return allTypes.filter((t) => isBlockAllowedInColumn(t, split, columnIndex));
}

